<template>
  <div class="azure-boards-step-2">
    <div class="row mb-3">
      <div class="col-12">
        <h2>Project Selection</h2>
      </div>
      <div class="col-12">
        <p>Please choose one project</p>
      </div>
    </div>

    <div v-if="loadError" class="error-state">
      <LucideIcon icon="TriangleAlert" class="error-icon"/>
      <p>{{ loadErrorMessage }}</p>
      <ButtonElement class="btn--ms btn--accent btn--md" @click="loadProjects()">
        <LucideIcon icon="RotateCw"/> Retry
      </ButtonElement>
    </div>

    <template v-else>
      <div class="row jira-feature-headline jf-row">
        <div class="col-6">
          <div class="jfh-col metastory">
            <div class="icon">
              <img class="metastory-logo light" src="@/assets/ui/text-cursor.svg" style="width: 6px;">
              <img class="metastory-logo dark" src="@/assets/ui/text-cursor-white.svg" style="width: 6px;">
            </div>
            metastory Projects
          </div>
        </div>
        <div class="col-6">
          <div class="jfh-col">
            <div class="icon">
              <img src="@/assets/integrations/azure.svg" style="height: 16px;">
            </div>
            Azure Boards Projects
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-6">
          <ul class="project-list scrollbar">
            <div class="loading-list" v-if="isLoading">
              <LucideIcon class="fa-spin-animation" icon="LoaderCircle"/>
              Fetching projects
            </div>
            <li
              class="project-item"
              v-for="project in allWorkspaceProjects"
              :key="project.id"
              @click="selectedMSProject = project"
              :class="[selectedMSProject.id === project.id ? 'selected' : '']"
            >
              {{ project.name }}
              <LucideIcon v-if="selectedMSProject.id === project.id" icon="Check"/>
            </li>
          </ul>
        </div>
        <div class="col-6">
          <ul class="project-list scrollbar">
            <div class="loading-list" v-if="isLoading">
              <LucideIcon class="fa-spin-animation" icon="LoaderCircle"/>
              Fetching projects
            </div>
            <div v-else-if="!isLoading && azureProjectList.length === 0" class="empty-state">
              <LucideIcon icon="FolderOpen" class="empty-icon"/>
              <p>No Azure Boards projects found.</p>
              <ul class="empty-hints">
                <li>Make sure the PAT has <strong>Project — Read</strong> scope</li>
                <li>Verify the organization name is correct</li>
                <li>Confirm the PAT belongs to that organization</li>
              </ul>
              <div class="empty-actions">
                <a href="https://dev.azure.com" target="_blank" rel="noopener noreferrer" class="empty-link">
                  Open Azure DevOps
                  <LucideIcon icon="ExternalLink"/>
                </a>
                <ButtonElement class="btn--ms btn--silent-tertiary btn--sm" @click="$emit('cancelClick')">
                  Edit credentials
                </ButtonElement>
              </div>
            </div>
            <li
              v-else
              class="project-item"
              v-for="project in azureProjectList"
              :key="project.id"
              @click="selectedAzureProject = project"
              :class="[selectedAzureProject.id === project.id ? 'selected' : '']"
            >
              {{ project.name }}
              <LucideIcon v-if="selectedAzureProject.id === project.id" icon="Check"/>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <div class="row">
      <div class="col-12">
        <div class="config-footer flex-end">
          <div class="button-group">
            <ButtonElement class="btn--ms btn--silent-tertiary btn--regular" @click="$emit('cancelClick')">Back</ButtonElement>
            <ButtonElement class="btn--ms btn--accent"
                           @click="clickContinue()"
                           :is-disable="!selectedMSProject.id || !selectedAzureProject.id || loadError">Continue
            </ButtonElement>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import ButtonElement from '@shared/components/elements/button/button.element.vue'
import {IntegrationNs, ProjectNs} from '@shared/types'
import {mapStores} from "pinia"
import {useWorkspaceStore} from "@shared/stores/workspace.store"
import {useIntegrationStore} from "@domains/settings/stores/integration.store"
import {useGeneralStore} from "@shared/stores/general.store"
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'AzureBoardsStep2',
  components: {
    ButtonElement,
  },
  emits: ['cancelClick', 'continueClick'],
  computed: {
    ...mapStores(useWorkspaceStore, useIntegrationStore, useGeneralStore),
  },
  data() {
    return {
      isLoading: false,
      loadError: false,
      loadErrorMessage: '',
      allWorkspaceProjects: [] as ProjectNs.Project[],
      selectedMSProject: {} as ProjectNs.Project,
      azureProjectList: [] as ProjectNs.Project[],
      selectedAzureProject: {} as ProjectNs.Project,
    }
  },
  beforeMount() {
    this.loadProjects()
  },
  methods: {
    loadProjects() {
      this.isLoading = true
      this.loadError = false
      const workspace = this.workspaceStore.getSelectedWorkspace
      const payload = {
        workspaceId: workspace.id,
        integrationType: IntegrationNs.IntegrationTypeEnums.AZURE,
      } as IntegrationNs.IntegrationInfoRequest

      Promise.all([
        this.integrationStore.loadNotIntegratedProjectsInWorkspace(payload),
        this.integrationStore.loadAllProjectsToIntegration(payload),
      ]).then(([msProjects, azureProjects]) => {
        this.isLoading = false
        this.allWorkspaceProjects = [
          {id: -99, name: 'New Blank Project +'} as ProjectNs.Project,
          ...msProjects,
        ]
        this.azureProjectList = azureProjects ?? []
      }).catch((error) => {
        this.isLoading = false
        const status = error?.response?.status
        if (status === 401) {
          toast.error('Session expired. Please reconnect your Azure Boards account.')
          this.$emit('cancelClick')
          return
        }
        this.loadError = true
        this.loadErrorMessage = error?.response?.data?.message ?? 'Failed to load projects. Please retry.'
      })
    },
    clickContinue() {
      if (!this.selectedMSProject || !this.selectedAzureProject) return
      const payload = {
        projectKey: this.selectedAzureProject.key,
        msProjectId: this.selectedMSProject.id,
      } as any
      this.$emit('continueClick', payload)
    },
  },
})
</script>

<style lang="scss" scoped>
.loading-list {
  padding: var(--space-16);
  font-size: var(--fs-14);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-8);
  padding: var(--space-32) var(--space-16);
  font-size: var(--fs-14);
  color: var(--text-neutral);

  .empty-icon {
    font-size: var(--fs-24);
  }

  .empty-hints {
    text-align: left;
    padding-left: var(--space-16);
    color: var(--text-secondary);
    font-size: var(--fs-13);
    margin: 0;
    line-height: 1.8;
  }

  .empty-actions {
    display: flex;
    align-items: center;
    gap: var(--space-12);
    flex-wrap: wrap;
    justify-content: center;
  }

  .empty-link {
    color: var(--accent-color);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: var(--space-4);

    &:hover {
      text-decoration: underline;
    }
  }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-12);
  padding: var(--space-32);
  text-align: center;
  font-size: var(--fs-14);
  color: var(--text-neutral);

  .error-icon {
    font-size: var(--fs-24);
    color: var(--danger-color);
  }
}
</style>
